<template>
    <div class="idp-report-card">
        <div class="report-card-header">
            <div class="report-info">
                <div class="report-date-badge">
                    <i class="bi bi-calendar-check"></i>
                    <span>{{ formatDate(report.created_date) }}</span>
                </div>
                <div class="report-quarter-badge">
                    {{ getQuarter(report.created_date) }}
                </div>
            </div>
            <div class="report-actions">
                <b-button size="sm" variant="outline-warning" @click="$emit('edit', report)" v-b-tooltip.hover
                    title="Edit this IDP">
                    <i class="bi bi-pencil"></i>
                    <span class="btn-text">Edit</span>
                </b-button>
            </div>
        </div>

        <!-- Skills Grid -->
        <div class="skills-grid">
            <IDPSkillPillar title="Technical" pillar-type="technical" :green-value="report.technical_green"
                :yellow-value="report.technical_yellow" :red-value="report.technical_red"
                :previous-report="previousReport" />
            <IDPSkillPillar title="Tactical" pillar-type="tactical" :green-value="report.tactical_green"
                :yellow-value="report.tactical_yellow" :red-value="report.tactical_red"
                :previous-report="previousReport" />
            <IDPSkillPillar title="Physical" pillar-type="physical" :green-value="report.physical_green"
                :yellow-value="report.physical_yellow" :red-value="report.physical_red"
                :previous-report="previousReport" />
            <IDPSkillPillar title="Mental" pillar-type="mental" :green-value="report.physcological_green"
                :yellow-value="report.physcological_yellow" :red-value="report.physcological_red"
                :previous-report="previousReport" />
        </div>

        <!-- Comments Section -->
        <div class="comments-section">
            <div class="comments-grid">
                <div class="comment-card player">
                    <div class="comment-header">
                        <i class="bi bi-person-fill"></i>
                        <h5>Player Comments</h5>
                    </div>
                    <p class="comment-text">{{ report.player_comments || 'No comments added.' }}</p>
                </div>
                <div class="comment-card coach">
                    <div class="comment-header">
                        <i class="bi bi-person-badge-fill"></i>
                        <h5>Coach Comments</h5>
                    </div>
                    <p class="comment-text">{{ report.coach_comments || 'No comments added.' }}</p>
                </div>
            </div>
        </div>

        <!-- Next Review Info -->
        <div class="review-info" v-if="nextReviewDate">
            <i class="bi bi-bell"></i>
            <span>Next review: <strong>{{ nextReviewDate }}</strong></span>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import IDPSkillPillar from './IDPSkillPillar.vue';

export default {
    name: 'IDPReportCard',
    components: {
        IDPSkillPillar
    },
    props: {
        report: {
            type: Object,
            required: true
        },
        previousReport: {
            type: Object,
            default: null
        },
        canEdit: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        nextReviewDate() {
            // Calculate next review (6 weeks from report date)
            if (!this.report.created_date) return null;
            const nextDate = moment(this.report.created_date).add(6, 'weeks');
            if (nextDate.isBefore(moment())) return null;
            return nextDate.format('DD MMM YYYY');
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('DD MMMM YYYY');
        },
        getQuarter(date) {
            const quarter = moment(date).quarter();
            return `Q${quarter}`;
        }
    }
};
</script>

<style scoped lang="scss">
.idp-report-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
}

.report-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e2e8f0;
}

.report-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.report-date-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #f1f5f9;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 500;
    color: #475569;

    i {
        color: #4a7c59;
    }
}

.report-quarter-badge {
    background: #3b82f6;
    color: #fff;
    padding: 0.375rem 0.75rem;
    border-radius: 16px;
    font-size: 0.75rem;
    font-weight: 600;
}

.report-actions {
    .btn-text {
        margin-left: 0.375rem;
    }
}

.skills-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.comments-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e2e8f0;
}

.comments-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
}

.comment-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1rem;

    &.player {
        border-top: 3px solid #3b82f6;
    }

    &.coach {
        border-top: 3px solid #8b5cf6;
    }
}

.comment-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    i {
        font-size: 1.125rem;
    }

    .player & i {
        color: #3b82f6;
    }

    .coach & i {
        color: #8b5cf6;
    }

    h5 {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 600;
        color: #334155;
    }
}

.comment-text {
    margin: 0;
    font-size: 0.9375rem;
    color: #64748b;
    line-height: 1.6;
}

.review-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #92400e;

    i {
        color: #d97706;
    }

    strong {
        color: #78350f;
    }
}

/* Tablet */
@media (max-width: 1024px) {
    .skills-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

/* Mobile */
@media (max-width: 768px) {
    .idp-report-card {
        padding: 1rem;
    }

    .report-card-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .report-actions {
        width: 100%;

        button {
            width: 100%;
            justify-content: center;
        }
    }

    .skills-grid {
        grid-template-columns: 1fr;
    }

    .comments-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .report-info {
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
    }

    .report-date-badge {
        width: 100%;
        justify-content: center;
    }

    .report-quarter-badge {
        width: 100%;
        text-align: center;
    }
}
</style>
